import { Fade, Link } from '@material-ui/core';
import { WarningRounded } from '@material-ui/icons';
import { parseDate } from '@superdispatch/dates';
import { useFormikEnhanced } from '@superdispatch/forms';
import {
  Color,
  ColorDynamic,
  Inline,
  Stack,
  SuperDispatchTheme,
  VisibilityObserver,
  useSnackbarStack,
} from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { FormikProvider } from 'formik';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { blurActiveElement } from 'shared/helpers/DomHelpers';
import { addLogContext, logInfo } from 'shared/helpers/Logger';
import { LocalStore } from 'shared/helpers/Store';
import { LightBulbIcon } from 'shared/icons/LightBulbIcon';
import { useLoadRequestSuggestionsNudging } from 'shared/modules/load-suggestions/LoadSuggestionsNudgingHelper';
import {
  LoadboardAccessDTO,
  useBecomeVerifiedCarrierNavigation,
  useLoadboardAccess,
} from 'shared/modules/loadboard/core/loadboard-access/LoadboardAccessAPI';
import { useLoadboardAccessContext } from 'shared/modules/loadboard/core/loadboard-access/LoadboardAccessContext';
import { PermissionAction } from 'shared/modules/permission/core/PermissionAction';
import { PermissionIcon } from 'shared/modules/permission/core/PermissionIcon';
import { PricingInsightsLink } from 'shared/modules/pricing-insights/PricingInsightsLink';
import { LimitedExperienceDialog } from 'shared/modules/shipper/LimitedExperienceDialog';
import { useShipperListHasLimitedExperience } from 'shared/modules/shipper/ShipperUtils';
import { useFlag } from 'shared/settings/FeatureToggles';
import styled from 'styled-components';
import { useLoadboardContext } from '../../data/LoadboardContext';
import { usePostingAPI } from '../../data/LoadboardPostingAPI';
import { PostingCarrierLoadDTO, PostingDTO } from '../../data/dto/PostingDTO';
import {
  PostingLoadDTO,
  isPostingStepBookable,
  mapPostingToPricingInsights,
  shouldReviewShipperRequirements,
} from '../../data/dto/PostingLoadDTO';
import {
  PostingRequestValuesDTO,
  postingRequestPayloadSchema,
  postingRequestValuesSchema,
} from '../../data/dto/PostingRequestDTO';
import { trackLoadboardEvent } from '../LoadboardAnalytics';
import {
  countRequestsSentBeforeLoadAlertNudging,
  usePostingLoadAlertNudgingTrigger,
} from '../nudging/PostingDetailsNudgeHelper';
import { useLargestTrailerCapacityIsValidValue } from './PostingLargestTrailerCapacityField';
import { PostingUnverifiedShipperConfirmationDialog } from './PostingUnverifiedShipperConfirmationDialog';
import { PostingVehicleStatus } from './PostingVehicleStatus';
import { PostingWaiver } from './PostingWaiver';
import { usePostingsPageContext } from './PostingsPageContext';
import { RequestFormFields } from './RequestFormFields';
import {
  BookPostingButton,
  RequestPostingButton,
} from './RequestPostingButton';

const Styled2 = styled.div`
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px;
  position: fixed;
  z-index: ${({ theme }: { theme: SuperDispatchTheme }) => theme.zIndex.modal};
  background-color: ${ColorDynamic.White};
`;

const BECOME_VERIFIED_LINK_VISIBILITY_STATUSES: Array<
  LoadboardAccessDTO['verification_status']
> = ['not_submitted', 'declined', 'revoked'];

function VerificationWarnings({ load }: { load: PostingLoadDTO }) {
  const { data: loadboardAccess } = useLoadboardAccess();
  const becomeVerifiedCarrier = useBecomeVerifiedCarrierNavigation();

  const shouldShowBecomeVerifiedLink =
    loadboardAccess &&
    loadboardAccess.access !== 'unlimited' &&
    !load.carrier_access.is_approved_by_shipper &&
    BECOME_VERIFIED_LINK_VISIBILITY_STATUSES.includes(
      loadboardAccess.verification_status,
    );

  const shouldShowRequirements =
    shouldReviewShipperRequirements(load) && !!load.shipper;

  if (!shouldShowBecomeVerifiedLink && !shouldShowRequirements) {
    return null;
  }

  return (
    <Stack>
      {shouldShowBecomeVerifiedLink && (
        <Inline verticalAlign="center">
          <WarningRounded fontSize="small" htmlColor={Color.Yellow300} />

          <Link
            href="#"
            color="primary"
            variant="body1"
            onClick={() => {
              becomeVerifiedCarrier({
                source: 'slb_load_details',
                backUrl: encodeURIComponent(window.location.href),
              });
            }}
          >
            Become Verified Carrier
          </Link>
        </Inline>
      )}

      {shouldShowRequirements && (
        <Inline verticalAlign="center">
          <WarningRounded fontSize="small" htmlColor={Color.Yellow300} />

          <TextBox variant="body-semibold">Review Shipper requirements</TextBox>
        </Inline>
      )}
    </Stack>
  );
}

interface SendRequestFormProps {
  isBookable: boolean;
  posting: PostingDTO;
  onScrollToFormClick?: () => void;
}

export function SendRequestForm({
  posting,
  isBookable,
  onScrollToFormClick,
  posting: { load },
}: SendRequestFormProps) {
  const { addSnackbar } = useSnackbarStack();
  const { bookPosting, requestPosting } = usePostingAPI();
  const {
    router: { closePostingScreen },
  } = useLoadboardContext();
  const { params } = usePostingsPageContext();
  const { openAccessDialog } = useLoadboardAccessContext();
  const { setLoadSuggestionsNudgingRequestedLoadGuid } =
    useLoadRequestSuggestionsNudging();

  const [openedTime, setOpenedTime] = useState(() => Date.now());
  const [currentModal, setCurrentModal] = useState<
    'unverified-shipper' | 'limited-experience' | undefined
  >();
  const isNewShipperAlertEnabled = useFlag('new_shipper_alert');
  const { hasLimitedExperienceShipper, experienceList } =
    useShipperListHasLimitedExperience(
      isNewShipperAlertEnabled ? [load.shipper?.guid] : [],
    );
  const isTrailerCapacityFormValid = useLargestTrailerCapacityIsValidValue();
  const triggerLoadAlertNudging = usePostingLoadAlertNudgingTrigger();
  const isCounterOffer = posting.carrier_load?.status === 'COUNTER_OFFER_SENT';

  const initialValues = useMemo((): PostingRequestValuesDTO => {
    const localDate = DateTime.local();
    const pickupDate = load.pickup.scheduled_at;
    const deliveryDate = load.delivery.scheduled_at || pickupDate;
    const lastCarrierName = LocalStore.get('last_carrier_contact_name');
    const isDatesPast =
      parseDate(pickupDate, { format: 'JodaISO' }) < localDate.startOf('day') ||
      parseDate(deliveryDate, { format: 'JodaISO' }) < localDate.startOf('day');

    return postingRequestValuesSchema.cast({
      price: isCounterOffer ? 0 : load.price,
      posting_guid: load.posting_guid,
      pickup_date: isDatesPast ? null : pickupDate,
      delivery_date: isDatesPast ? null : deliveryDate,
      carrier_contact_name:
        !lastCarrierName || lastCarrierName === '""' ? '' : lastCarrierName,
    });
  }, [load, isCounterOffer]);

  const formik = useFormikEnhanced<
    PostingRequestValuesDTO,
    PostingCarrierLoadDTO
  >({
    initialValues,
    validationSchema: postingRequestValuesSchema,
    onSubmit(values) {
      const isBook = shouldSendBookingRequest();
      const payload = postingRequestPayloadSchema.cast({
        ...values,
        is_counter_bid: isCounterOffer,
      });

      // Book endpoint has no comment field; omit it so we don't send a key it
      // silently drops.
      if (isBook) {
        delete payload.carrier_comment;
      }

      // Blur of input on mobiles to hide keyboard.
      blurActiveElement();

      addLogContext('send_request_values', payload);
      trackLoadboardEvent({
        name: 'SLB: Request/Book Time',
        type: isBook ? 'book' : 'request',
        openTime: openedTime,
        postedToLoadboardAt: load.posted_to_loadboard_at,
      });
      logInfo('Submit load request form', { isBook });

      return isBook
        ? bookPosting(load.guid, payload)
        : requestPosting(load.guid, payload);
    },
    onSubmitSuccess(_, values) {
      countRequestsSentBeforeLoadAlertNudging();
      const payload = postingRequestValuesSchema.cast(values);
      const isBook = shouldSendBookingRequest();

      if (isBook) {
        addSnackbar('Booked', { variant: 'success' });

        trackLoadboardEvent({
          posting,
          params,
          name: 'SLB: Booked Load',
          bookQuery: payload,
        });
      } else {
        setLoadSuggestionsNudgingRequestedLoadGuid(load.guid);

        if (isBid()) {
          if (!LocalStore.get('has_seen_counter_offer_message')) {
            LocalStore.set('has_seen_counter_offer_message', 'true');
            addSnackbar(
              <Inline verticalAlign="center">
                <LightBulbIcon />

                <TextBox color="white">
                  You may receive a Counter Offer <br /> to your Bid from the
                  Shipper
                </TextBox>
              </Inline>,
              { key: 'CounterOfferMessage', autoHideDuration: null },
            );
          }

          addSnackbar('Bid sent to shipper for review', { variant: 'success' });
        } else {
          addSnackbar('Request sent to shipper for review', {
            variant: 'success',
          });
        }

        trackLoadboardEvent({
          posting,
          params,
          name: 'SLB: Requested Load',
          bookQuery: payload,
        });
      }

      triggerLoadAlertNudging(isBook, load);
      closePostingScreen();
      LocalStore.set('last_carrier_contact_name', payload.carrier_contact_name);
    },
  });

  useEffect(() => {
    setOpenedTime(Date.now());
  }, [load.guid]);

  function isBid() {
    return formik.values.price !== load.price;
  }

  function shouldSendBookingRequest(): boolean {
    if (!isBookable) {
      return false;
    }

    const { values } = formik;

    if (values.price !== initialValues.price) {
      return false;
    }

    if (!isPostingStepBookable(values.pickup_date, load.pickup)) {
      return false;
    }

    if (!isPostingStepBookable(values.delivery_date, load.delivery)) {
      return false;
    }

    return true;
  }

  return (
    <>
      <PostingUnverifiedShipperConfirmationDialog
        notes={load.shipper?.external_notes}
        open={currentModal === 'unverified-shipper'}
        onClose={() => {
          setCurrentModal(undefined);
        }}
        onConfirm={() => {
          setCurrentModal(undefined);
          formik.handleSubmit();
        }}
      />
      {load.shipper && experienceList && (
        <LimitedExperienceDialog
          shippersExperience={experienceList}
          action="send_request"
          isOpen={currentModal === 'limited-experience'}
          onCancel={() => {
            setCurrentModal(undefined);
          }}
          onConfirm={() => {
            setCurrentModal(undefined);
            formik.handleSubmit();
          }}
        />
      )}
      <FormikProvider value={formik}>
        <VisibilityObserver
          render={({ ref, visibility }) => (
            <form
              onSubmit={(event) => {
                event.preventDefault();

                if (!isTrailerCapacityFormValid) {
                  return;
                }

                if (!load.carrier_access.can_request) {
                  openAccessDialog(closePostingScreen);
                  return;
                }

                if (load.shipper?.verification_status === 'NOT_APPROVED') {
                  setCurrentModal('unverified-shipper');
                  return;
                }

                if (hasLimitedExperienceShipper) {
                  setCurrentModal('limited-experience');
                  return;
                }

                formik.handleSubmit(event);
              }}
            >
              <Stack space="small">
                <RequestFormFields
                  load={load}
                  isBooking={shouldSendBookingRequest()}
                />

                <PostingVehicleStatus load={load} />

                <VerificationWarnings load={load} />

                <Stack space="xsmall">
                  {onScrollToFormClick && (
                    <Fade in={visibility !== 'visible'}>
                      <Styled2>
                        {shouldSendBookingRequest() ? (
                          <BookPostingButton
                            fullWidth={true}
                            price={formik.values.price}
                            onClick={onScrollToFormClick}
                          />
                        ) : (
                          <RequestPostingButton
                            fullWidth={true}
                            price={formik.values.price}
                            onClick={onScrollToFormClick}
                            title={isBid() ? 'Place Bid' : 'Send Request'}
                          />
                        )}
                      </Styled2>
                    </Fade>
                  )}

                  <PermissionAction name="marketplace" permission="can_manage">
                    <div ref={ref}>
                      {shouldSendBookingRequest() ? (
                        <BookPostingButton
                          type="submit"
                          fullWidth={true}
                          startIcon={
                            <PermissionIcon
                              name="marketplace"
                              permission="can_manage"
                            />
                          }
                          pending={formik.isSubmitting}
                          price={formik.values.price}
                        />
                      ) : (
                        <RequestPostingButton
                          type="submit"
                          fullWidth={true}
                          pending={formik.isSubmitting}
                          price={formik.values.price}
                          startIcon={
                            <PermissionIcon
                              name="marketplace"
                              permission="can_manage"
                            />
                          }
                          title={isBid() ? 'Place Bid' : 'Send Request'}
                        />
                      )}
                    </div>
                  </PermissionAction>

                  <Inline horizontalAlign="center">
                    <PricingInsightsLink
                      source="Load Details"
                      data={mapPostingToPricingInsights(posting.load)}
                    />
                  </Inline>

                  <PostingWaiver />
                </Stack>
              </Stack>
            </form>
          )}
        />
      </FormikProvider>
    </>
  );
}
